import classNames from 'classnames';
import _ from 'lodash';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@kep-ui-kit/ui-components';
import styled from '@emotion/styled';
import {
  invoiceSubscriptionTypes,
  InvoiceColumnTypes,
  invoiceColumnNames,
} from '@selfserve/react-utils';

export const StyledTableHead = styled(TableHead)(
  ({ theme }: { theme?: any }) => ({
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  })
);

export const StyledTable = styled(Table)(({ theme }: { theme?: any }) => ({
  [theme.breakpoints.down('md')]: {
    borderCollapse: 'collapse',
  },
}));

export const StyledTableRow = styled(TableRow)(
  ({ theme }: { theme?: any }) => ({
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexWrap: 'wrap',
      borderBottom: '1px solid #C4C4C4',
    },
  })
);

export const StyledTableCell = styled(TableCell)(
  ({ theme, status }: { theme?: any; status?: string }) => ({
    '&.custom-table-cell': {
      fontSize: '14px',
      fontFamily: 'Source Sans Pro',
      paddingLeft: '20px',
    },
    '&.custom-table-cell-body': {
      color: '#333',
      fontSize: '16px',
      paddingTop: '20px',
      paddingBottom: '20px',
      fontWeight: '400',
      ...(status === invoiceSubscriptionTypes.UNPAID
        ? {
            color: '#E12437',
          }
        : ''),
      [`&.${invoiceColumnNames.ISSUED_DATE}`]: {
        fontWeight: '600',
      },
      [`&.${invoiceColumnNames.ICON}`]: {
        textAlign: 'left',
        width: '30px',
        minWidth: '30px',
      },
    },
    '.export-button': {
      color: '#006EFA',
    },
    [theme.breakpoints.down('sm')]: {
      '&.custom-table-cell': {
        fontSize: '16px',
        borderBottomWidth: 0,
        minWidth: 'auto',
      },
      [`
        &.${invoiceColumnNames.INVOICE_NUMBER},
        &.${invoiceColumnNames.AMOUNT},
        &.${invoiceColumnNames.STATUS},
        &.${invoiceColumnNames.ICON}
      `]: {
        padding: '0 16px 20px',
        height: 'fit-content',
      },
      [`&.${invoiceColumnNames.ICON}`]: {
        padding: 0,
        margin: '-40px 20px 0 auto',
      },
      [`&.${invoiceColumnNames.ISSUED_DATE}`]: {
        paddingLeft: '16px',
        width: '100%',
      },
    },
  })
);

interface Column {
  id: InvoiceColumnTypes;
  label: string;
  minWidth?: number;
  format?: (value: number) => string;
}

export const columns: Column[] = [
  {
    id: invoiceColumnNames.ISSUED_DATE as InvoiceColumnTypes,
    label: 'Issued Date',
  },
  {
    id: invoiceColumnNames.INVOICE_NUMBER as InvoiceColumnTypes,
    label: 'Invoice Number',
  },
  {
    id: invoiceColumnNames.AMOUNT as InvoiceColumnTypes,
    label: 'Amount',
  },
  {
    id: invoiceColumnNames.STATUS as InvoiceColumnTypes,
    label: 'Status',
  },
  {
    id: invoiceColumnNames.ICON as InvoiceColumnTypes,
    label: '',
  },
];

export const TableHeadContent = () => (
  <StyledTableHead className="p-datatable-thead">
    <StyledTableRow>
      {columns.map((column) => (
        <StyledTableCell
          key={column.id}
          style={{ minWidth: column.minWidth }}
          className="custom-table-cell"
        >
          {column.label}
        </StyledTableCell>
      ))}
    </StyledTableRow>
  </StyledTableHead>
);

export type Data = {
  [key in InvoiceColumnTypes]: string | JSX.Element;
};

export const TableBodyContent = ({ rows }: { rows: Data[] }) => {
  return (
    <TableBody className="p-datatable-tbody">
      {rows.map((row) => {
        return (
          <StyledTableRow
            hover
            tabIndex={-1}
            key={row[invoiceColumnNames.INVOICE_NUMBER] as string}
            className="table-body-row"
          >
            {columns.map((column) => {
              const value = row[column.id];
              return (
                <StyledTableCell
                  key={column.id}
                  className={classNames(
                    `custom-table-cell custom-table-cell-body ${column.id}`,
                    {
                      'date-column':
                        column.id === invoiceColumnNames.ISSUED_DATE,
                    }
                  )}
                  status={
                    column.id === invoiceColumnNames.STATUS
                      ? (value as string)
                      : undefined
                  }
                >
                  {value}
                </StyledTableCell>
              );
            })}
          </StyledTableRow>
        );
      })}
    </TableBody>
  );
};
